import React from "react";
import { Box, Typography, keyframes, Icon } from "@mui/material";
import { useTheme } from "@emotion/react";

// Slide down animation for the notification
const slideDown = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Animation for the "Rain Delay" appearing - same as TransparentUpdatesIcon
const fadeInUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Pulse animation for emphasis
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

const FasterUpdatesIcon = ({ variant = "light" }) => {
  const theme = useTheme();
  const { primary, overlay } = theme.palette;
  const isDark = variant === "dark";

  // Colors based on variant - all grayscale now
  const notificationBg = isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(255, 255, 255, 0.95)";
  const placeholderColor = isDark ? overlay.white30 : "rgba(255, 255, 255, 0.3)";
  const iconColor = isDark ? overlay.white50 : "rgba(255, 255, 255, 0.6)";
  const timeColor = isDark ? overlay.white60 : "rgba(255, 255, 255, 0.7)";
  const alertBg = isDark ? overlay.white20 : "rgba(255, 255, 255, 0.2)";
  const alertText = isDark ? primary.contrastText : "rgba(255, 255, 255, 0.9)";
  const edgeLineColor = "rgba(255, 255, 255, 0.3)";
  const backgroundColor = isDark ? "rgba(0, 0, 0, 0.3)" : "rgba(0, 0, 0, 0.4)";

  return (
    <Box
      sx={{
        position: "relative",
        width: "200px",
        height: "140px",
        margin: "0 auto",
        backgroundColor: backgroundColor,
        backdropFilter: "blur(10px)",
        borderRadius: "12px",
        display: "flex",
        flexDirection: "column",
        padding: "16px",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {/* Left edge line */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "1px",
          backgroundColor: edgeLineColor,
        }}
      />
      
      {/* Right edge line */}
      <Box
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          width: "1px",
          backgroundColor: edgeLineColor,
        }}
      />

      {/* Notification Card */}
      <Box
        sx={{
          width: "90%",
          backgroundColor: notificationBg,
          borderRadius: "12px",
          padding: "12px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
          backdropFilter: "blur(20px)",
          animation: `${slideDown} 0.8s ease-out`,
          border: "1px solid rgba(255, 255, 255, 0.2)",
        }}
      >
        {/* Notification Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            mb: "8px",
          }}
        >
          {/* Calendar icon - same as TransparentUpdatesIcon */}
          <Icon
            baseClassName="material-symbols-outlined"
            sx={{
              fontSize: "16px",
              color: iconColor,
              opacity: 0.8,
              fontWeight: 300,
            }}
          >
            calendar_month
          </Icon>

          {/* Title placeholder bar */}
          <Box
            sx={{
              flex: 1,
              height: "6px",
              marginTop: "1px",
              backgroundColor: placeholderColor,
              borderRadius: "5px",
            }}
          />

          {/* Right-aligned "now" */}
          <Typography
            sx={{
              fontSize: "9px",
              color: timeColor,
              lineHeight: 1,
              fontWeight: 400,
            }}
          >
            now
          </Typography>
        </Box>

        {/* Rain Delay Alert - styled like "Last updated" */}
        <Box
          sx={{
            backgroundColor: alertBg,
            borderRadius: "6px",
            padding: "6px 8px",
            animation: `${fadeInUp} 1s ease-out 0.5s both, ${pulse} 2s ease-in-out 1.5s infinite`,
            backdropFilter: "blur(10px)",
          }}
        >
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              color: alertText,
              lineHeight: 1,
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <Icon
              baseClassName="material-symbols-outlined"
              sx={{
                fontSize: "12px",
                fontWeight: 300,
              }}
            >
              warning
            </Icon>
            Rain Delay
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default FasterUpdatesIcon; 